import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { Button } from './ui/button';
import { Badge } from './ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from './ui/dialog';
import { FileText, ExternalLink, ShieldCheck, Bot, PenLine } from 'lucide-react';
import { toast } from 'sonner';
import { fmtDateTime } from '../lib/datetime';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const fmtMoney = (n) => '$ ' + Number(n || 0).toLocaleString('es-AR', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
const fmtDate = (iso) => { if (!iso) return '-'; const p = String(iso).slice(0, 10).split('-'); return p.length === 3 ? p.reverse().join('/') : iso; };

const Row = ({ label, children }) => (
  <div className="grid grid-cols-[140px_1fr] gap-3 py-1.5 border-b last:border-0 text-sm">
    <span className="text-muted-foreground">{label}</span>
    <span>{children}</span>
  </div>
);

export function JournalEntryAuditDialog({ entryId, onClose }) {
  const navigate = useNavigate();
  const [data, setData] = useState(null);

  useEffect(() => {
    if (!entryId) { setData(null); return; }
    axios.get(`${BACKEND_URL}/api/accounting/entries/${entryId}/audit`, { withCredentials: true })
      .then(r => setData(r.data))
      .catch(() => { toast.error('No se pudo cargar la auditoría del asiento'); onClose(); });
  }, [entryId]); // eslint-disable-line react-hooks/exhaustive-deps

  const doc = data?.document;
  const rev = data?.reversal;

  return (
    <Dialog open={!!entryId} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-xl" data-testid="entry-audit-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <ShieldCheck className="w-5 h-5 text-blue-600" />
            Auditoría del asiento #{data?.entry?.entry_number ?? '…'}
          </DialogTitle>
          <DialogDescription>
            {data ? `${fmtDate(data.entry.date)} · ${data.entry.description}` : 'Cargando…'}
          </DialogDescription>
        </DialogHeader>

        {data && (
          <div className="space-y-4">
            <div>
              <p className="text-xs font-semibold uppercase text-muted-foreground mb-1">Generación</p>
              <Row label="Origen">
                {data.created.mode === 'manual'
                  ? <span className="inline-flex items-center gap-1.5"><PenLine className="w-3.5 h-3.5" />Asiento manual</span>
                  : <span className="inline-flex items-center gap-1.5"><Bot className="w-3.5 h-3.5" />Generado automáticamente desde {doc?.module}</span>}
              </Row>
              <Row label="Usuario">{data.created.by_name}</Row>
              <Row label="Fecha y hora">{data.created.at ? fmtDateTime(data.created.at) : '-'}</Row>
              {data.created.notes && <Row label="Notas">{data.created.notes}</Row>}
            </div>

            {doc?.source_id && (
              <div>
                <p className="text-xs font-semibold uppercase text-muted-foreground mb-1">Comprobante origen</p>
                <Row label="Módulo"><Badge variant="outline">{doc.module}</Badge></Row>
                <Row label="Comprobante">{doc.reference || '-'}</Row>
                {doc.date && <Row label="Fecha">{fmtDate(doc.date)}</Row>}
                {doc.entity && <Row label="Proveedor / Residente">{doc.entity}</Row>}
                {doc.amount != null && <Row label="Importe">{fmtMoney(doc.amount)}</Row>}
                {doc.status && <Row label="Estado"><Badge variant="outline">{doc.status}</Badge></Row>}
                {(!doc.exists || doc.deleted) && (
                  <div className="mt-2 rounded-md border border-amber-300 bg-amber-50 dark:bg-amber-950/30 p-3 text-xs text-amber-900 dark:text-amber-200" data-testid="audit-doc-deleted">
                    <b>El comprobante fue eliminado.</b>
                    {rev?.reversed_by_entry_number && <> El asiento quedó reversado por el <b>#{rev.reversed_by_entry_number}</b>.</>}
                    {doc.deleted_by_name && <span className="block mt-0.5">Eliminado por {doc.deleted_by_name}.</span>}
                    {doc.deletion_reason && <span className="block mt-0.5">Motivo: {doc.deletion_reason}</span>}
                  </div>
                )}
              </div>
            )}

            {(rev?.is_reversed || rev?.reverses_entry_number) && (
              <div>
                <p className="text-xs font-semibold uppercase text-muted-foreground mb-1">Reverso</p>
                {rev.reverses_entry_number && <Row label="Reversa al">Asiento #{rev.reverses_entry_number}</Row>}
                {rev.is_reversed && <Row label="Reversado por">Asiento #{rev.reversed_by_entry_number || '-'}</Row>}
                {rev.reversed_by_name && <Row label="Usuario">{rev.reversed_by_name}</Row>}
                {rev.reversed_at && <Row label="Fecha y hora">{fmtDateTime(rev.reversed_at)}</Row>}
                {rev.reversal_reason && <Row label="Motivo">{rev.reversal_reason}</Row>}
              </div>
            )}
          </div>
        )}

        <DialogFooter className="gap-2">
          {doc?.pdf_url && (
            <Button variant="outline" onClick={() => window.open(`${BACKEND_URL}${doc.pdf_url}`, '_blank')} data-testid="audit-open-pdf">
              <FileText className="w-4 h-4 mr-2" />Ver PDF del comprobante
            </Button>
          )}
          {doc?.route && (
            <Button variant="outline" onClick={() => { onClose(); navigate(doc.route); }} data-testid="audit-open-module">
              <ExternalLink className="w-4 h-4 mr-2" />Ir al módulo
            </Button>
          )}
          <Button onClick={onClose} data-testid="audit-close">Cerrar</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

export function EntryAuditButton({ entryId, onOpen, testId }) {
  return (
    <button type="button" onClick={() => onOpen(entryId)} data-testid={testId}
      className="inline-flex items-center gap-1 rounded-full border px-2 py-0.5 text-xs text-muted-foreground hover:bg-muted hover:text-foreground transition-colors">
      <ShieldCheck className="w-3 h-3" />Auditoría
    </button>
  );
}
